import React, { useEffect, useState } from 'react';

import { onEnterAction } from '../utils/a11y';

export interface Props {
  mainLinksArray: {
    link: string,
    trackingTag: string,
    label: string,
  }[];
  navigationCallback(path: string): void;
  trackingCallback(event: string, params?: Record<string, unknown>): Promise<void>;
}

export const MainLinks = ({ mainLinksArray, trackingCallback, navigationCallback }: Props): JSX.Element => {
  const [currentURL, setCurrentURL] = useState<string>(window.location.href);

  useEffect(() => {
      const handleURLChange = (): void => {
          setCurrentURL(window.location.pathname);
      };

      // Listen for popstate events (e.g., browser back/forward navigation)
      window.addEventListener('popstate', handleURLChange);

      // Override pushState and replaceState to detect changes
      const originalPushState = history.pushState.bind(history);
      history.pushState = (...args): void => {
          originalPushState.apply(window, args);
          handleURLChange();
      };

      const originalReplaceState = history.replaceState.bind(history);
      history.replaceState = (...args): void => {
          originalReplaceState.apply(window, args);
          handleURLChange();
      };

      // Listen for hashchange events
      window.addEventListener('hashchange', handleURLChange);

      // Initial URL check
      handleURLChange();

      // Cleanup listeners on unmount
      return ():void => {
          window.removeEventListener('popstate', handleURLChange);
          window.removeEventListener('hashchange', handleURLChange);

          // Restore original pushState and replaceState methods
          history.pushState = originalPushState;
          history.replaceState = originalReplaceState;
      };
  }, []);

  const handleLinkClicked = async (eName: string, linkPath: string): Promise<void> => {
    const trackingProps = {
      elementId: eName,
    };

    await trackingCallback('clicked-button', trackingProps);
    navigationCallback(linkPath);
  };

  return (
    <div>
      {mainLinksArray.map((mainLink) => {
        const linkPath = new URL(mainLink.link, window.location.origin).pathname;
        const isActive = linkPath === currentURL;

        const linkClasses = `py-1 md:py-2 px-3 md:px-2 lg:p-2.5 block md:inline-block cursor-pointer ${isActive
          ? 'text-sapphire font-bold' : 'text-gray-700 hover:text-sapphire-700'}`;

        return (
          <div
            className={linkClasses}
            tabIndex={0}
            onClick={async (): Promise<void> => handleLinkClicked(mainLink.trackingTag, linkPath)}
            onKeyDown={(e): void => onEnterAction(e, () => handleLinkClicked(mainLink.trackingTag, linkPath))}
            key={mainLink.label}
            aria-label={`Link to ${mainLink.label}`}
            role='link'
          >
            {mainLink.label}
          </div>
        );
      })}
    </div>
  );
};
